/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {signal} from '@preact/signals-react';
import {createMutator} from '@ion/applications/src/store/SignalUtils';

/**
 * Counts the History reports the Time Mode control has let run - its own
 * Apply, and each re-run it accepts after the filter changes. While the
 * control is running a report, the History view re-queries on this count
 * rather than on the filter itself, so a change the control refuses over
 * the limit leaves the report showing its previous results.
 */
export const historyReportRevision = signal(0);

/**
 * Replaces the History report revision.
 */
export const setHistoryReportRevision = createMutator(historyReportRevision);

/**
 * Gets the History report revision.
 */
export const getHistoryReportRevision = () => historyReportRevision.value;

/**
 * Marks a History report as let run, so the History view re-queries under
 * the filters as they now stand.
 *
 * ```typescript
 * import {acceptHistoryReport} from '@/routes/tracking2/map/containers/store';
 *
 * ...
 *
 * acceptHistoryReport();
 * ```
 */
export const acceptHistoryReport = () => {
  setHistoryReportRevision(historyReportRevision.value + 1);
};

/**
 * Subscribes to History report revision changes.
 */
export const subscribeHistoryReportRevision = (onStoreChange: () => void) => (
  historyReportRevision.subscribe(() => {
    onStoreChange();
  })
);

/**
 * Defines a count of the assets the map shows under the applied filters.
 */
export interface TimeModeLayerCount {
  // the applied filters the count was taken under, as the Tracking Filter
  // keys its own counts - so it is only ever taken up for those filters ...
  key: string;
  assets: number;
}

/**
 * The assets the Time Mode control last counted off the map while reporting
 * "{{mode}} wasn't updated" - so the Tracking Filter, opened on those same
 * filters, can warn at once rather than wait on the backend for a count the
 * map already has. `null` until such a count is taken, and again once the
 * map goes back to Live.
 */
export const timeModeLayerCount = signal<TimeModeLayerCount | null>(null);

/**
 * Replaces the Time Mode layer count.
 */
export const setTimeModeLayerCount = createMutator(timeModeLayerCount);

/**
 * Gets the Time Mode layer count.
 */
export const getTimeModeLayerCount = () => timeModeLayerCount.value;

/**
 * Tracks whether the running Timeline or History report was kept on its
 * previous results - the filter changed under it to more assets than the
 * limit lets it re-run over (or to none) - so the applied filter bar shows
 * its Time Mode tag as a warning. It holds until the report runs again or
 * the map goes back to Live, whether or not the "{{mode}} wasn't updated"
 * card is still up.
 */
export const timeModeReportStale = signal(false);

/**
 * Replaces whether the running Time Mode report is stale.
 */
export const setTimeModeReportStale = createMutator(timeModeReportStale);
